<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <div>
      <ProgressBar
        v-if="!isLiteView()"
        :active-step="pageTitle"
      />
      <PageHeading :default-heading="pageTitle" />
      <form @submit.prevent>
        <!-- Account details -->
        <div class="pvd-container container-divider">
          <h2 class="pvd-heading-L">
            Account details
          </h2>
          <div class="pvd-display-row">
            <div class="display-row-label">
              <p>Account type</p>
            </div>
            <div class="display-row-content">
              <p>{{ pd_content.account_type }}</p>
            </div>
          </div>
        </div>
        <!-- Trust information details -->
        <div class="pvd-container container-divider">
          <h2
            class="pvd-heading-L"
          >
            Trust information
            <EditLink
              v-if="!showTaxDisclousureAndNotice"
              page-name="trust-information"
              is-not-istock="true"
              future-destination-state="trust-review-and-confirm"
            />
          </h2>
          <apx-text-input
            label="Trust name"
            :value="acc_details.trustName"
            readonly
          />
          <apx-text-input
            v-if="acc_details.isIrrevocable"
            label="Type of trust"
            value="Irrevocable"
            readonly
          />
          <div class="pvd-form-row">
            <div class="form-row-label form-row-label-top">
              <label v-if="routeId === 'managed'">Beneficiary name</label>
              <label v-else>For the Benefit of</label>
            </div>
            <div class="form-row-content">
              <p
                v-for="(beneficiary, index) in beneficiaries"
                :key="index+'bene'"
                class="mt-0"
              >
                {{ beneficiary.firstName+' '+(beneficiary.middleName ? beneficiary.middleName+' ' : '')+beneficiary.lastName }}
              </p>
            </div>
          </div>
          <apx-ssn
            :label="acc_details.taxIDType && acc_details.taxIDType.toUpperCase() === 'TIN' ?
              'Trust taxpayer ID number' :
              'Grantor Social Security number'"
            :value="acc_details.taxIDType && acc_details.taxIDType.toUpperCase() === 'TIN' ? acc_details.tin : acc_details.ssn"
            mask-regex="\d\d\d-\d\d-"
            :mask-pattern="maskSsn"
            readonly
          />
          <apx-date-select
            group-label="Date of trust"
            :date-input-value="acc_details.dateOfTrust"
            readonly
          />
          <SelectStateComponent
            :trust-state.sync="acc_details.trustState"
            :id-prefix="'trustState'"
            name="trustState"
          />
          <ReadbackComponent
            id="trust-address"
            label="Trust address"
            :values="[trustAddress.line1, trustAddress.line2,
                      `${trustAddress.city}, ${trustAddress.state} ${trustAddress.zip}`]"
          />
          <ReadbackComponent v-if="routeId === 'trust'"
            id="irs-backup-withholding"
            label="IRS backup withholding"
            :values="acc_details.backupWithHolding === 'Yes' ? ['Yes'] : ['I have not been notified by the IRS about backup withholdings']"
          />
        </div>
        <!-- Personal information details -->
        <div class="pvd-container container-divider">
          <h2
            class="pvd-heading-L"
          >
            Your personal information
            <EditLink
              v-if="!showTaxDisclousureAndNotice"
              page-name="personal-information"
              is-not-istock="true"
              future-destination-state="trust-review-and-confirm"
            />
          </h2>
          <apx-full-name
            group-label="Name"
            :first-name="pu_firstName"
            :middle-name="pu_middleName"
            :last-name="pu_lastName"
            readonly
          />
          <apx-ssn
            label="Social Security number"
            :value="showSecurityNumber"
            mask-regex="\d\d\d-\d\d-"
            :mask-pattern="maskSsn"
            readonly
          />
          <apx-date-select
            group-label="Date of birth"
            :date-input-value="pu_dob"
            readonly
          />
          <apx-select
            :value="residenceCountryName(pu_residenceCountry)"
            label="Country of citizenship"
            readonly
          />
          <apx-phone
            telephone-number-label="Mobile number"
            :telephone-number="pu_dayPhone"
            readonly
          />
          <apx-email
            label="Email"
            :value="pu_email"
            readonly
          />
          <ReadbackComponent
            id="pu-address"
            label="Legal residential address"
            :values="[pu_leg_line1, pu_leg_line2, cityStateZip(pu_leg_city,pu_leg_state,pu_leg_zip)]"
          />
        </div>

        <!-- Your trust role details -->
        <div class="pvd-container container-divider">
          <h2
            class="pvd-heading-L"
          >
            Your role
            <EditLink
              page-name="trust-role"
              is-not-istock="true"
              future-destination-state="trust-review-and-confirm"
            />
          </h2>
          <apx-text-input
            :value="pu_additionalDetails.role === 'trustee'? 'Trustee': 'Trustee and grantor'"
            label="Role in trust"
            readonly
          />
          <div
            v-if="pu_additionalDetails.hasAmendRevoke || pu_additionalDetails.hasAppointRemove"
            class="pvd-form-row"
          >
            <div class="form-row-label form-row-label-top">
              <label>Authorities</label>
            </div>
            <div class="form-row-content">
              <p
                v-if="pu_additionalDetails.hasAmend"
                style="margin-top:0"
              >
                Can amend conditions of the trust
              </p>
              <p
                v-if="pu_additionalDetails.hasRevoke"
                style="margin-top:0"
              >
                Can revoke conditions of the trust
              </p>
              <p
                v-if="pu_additionalDetails.hasAppointRemove"
                style="margin-top:0"
              >
                Can appoint/remove trustees
              </p>
            </div>
          </div>
        </div>
        <!-- Employement information details -->
        <div class="pvd-container container-divider">
          <h2
            class="pvd-heading-L"
          >
            Your employment information
            <EditLink
              page-name="employment-information"
              is-not-istock="true"
              future-destination-state="trust-review-and-confirm"
            />
          </h2>
          <apx-select
            :value="pd_content.employment_status_mapping[pu_employmentStatus]"
            label="Employment status"
            readonly
          />
          <apx-text-input
            v-if="showEmployerInfo(pu_employmentStatus)"
            :value="pu_occupation? pu_occupation : 'None'"
            label="Occupation"
            readonly
          />
          <apx-text-input
            v-if="showEmployerInfo(pu_employmentStatus)"
            :value="pu_employersName? pu_employersName : 'None'"
            label="Company name"
            readonly
          />
          <ReadbackComponent
            v-if="showEmployerInfo(pu_employmentStatus)"
            id="pu-employer-address"
            label="Company address"
            :values="employerAddress"
          />
          <ReadbackComponent
            v-if="pu_employmentStatus != 'Z'"
            :id="'associated-company-info'"
            label="Investment industry association"
            :values="pu_coAffillName ? [pu_coAffillName] : ['None']"
            show-help
            help-header="Investment industry association"
            help-body="Our records indicate that you previously provided this information in response to
              the following question: “Are you, or an immediate family member residing in the same household,
              employed by or associated with any of the following: a broker-dealer, stock exchange, exchange member firm,
              the Financial Industry Regulatory Authority (FINRA), a municipal securities dealer, or other financial
              institution?”. If that is still accurate, you do not need to do anything more. If anything has changed,
              please use the “Edit” link to update your answer."
          />
          <ReadbackComponent
            :id="'corporate-control-status'"
            label="Corporate control status"
            :values="pu_coDirectorSymbol ? [pu_coDirectorSymbol] : ['None']"
            show-help
            help-header="Corporate control status"
            help-body="Our records indicate that you previously provided this information in response to the following
             question: “Are you, your spouse, or any of your relatives, which includes, but is not limited to, parents,
             in-laws, and dependents, living in your home (at the same address) a member of the board of directors, a
             10% shareholder, a policy-making officer, or someone can direct the management and policies of a publically
             traded company.” If that is still accurate, you do not need to do anything more. If anything has changed,
             please use the “Edit” link to update your answer."
          />
        </div>
        <!-- Additional trustee(s) information details -->
        <div
          v-for="(additionalRole, index) in additionalRoles"
          :key="index+'role'">
        <div v-if="additionalRole.role !== 'authority'" class="pvd-container container-divider">
          <h2
            class="pvd-heading-L"
          >
            {{ additionalRole.firstName }} {{ additionalRole.middleName }} {{ formattedTrusteeLastName(additionalRole.lastName) }}
            personal information
            <EditLink
              :page-name="additionalRole.role == 'authority' ? 'trust-authority' : 'trust-additional-role'"
              is-not-istock="true"
              :user-index="index"
              future-destination-state="trust-review-and-confirm"
            />
          </h2>
          <apx-text-input
            :value="additionalRole.role === 'trustee' ? 'Trustee':
              additionalRole.role === 'grantor'? 'Grantor':
              additionalRole.role === 'authority'? 'Additional authority':
              'Trustee and grantor'"
            label="Role in trust"
            readonly
          />
          <apx-text-input
            v-if="additionalRole.isDeceased && additionalRole.isDeceased.toUpperCase() === 'YES'"
            value="Deceased"
            label="Status"
            readonly
          />
          <div
            v-if="additionalRole.hasAmendRevoke || additionalRole.hasAppointRemove"
            class="pvd-form-row"
          >
            <!--  Additional trustee(s) authority details -->
            <div class="form-row-label form-row-label-top">
              <label>Authorities</label>
            </div>
            <div class="form-row-content">
              <p
                v-if="additionalRole.hasAmend"
                style="margin-top:0"
              >
                Can amend conditions of the trust
              </p>
              <p
                v-if="additionalRole.hasRevoke"
                style="margin-top:0"
              >
                Can revoke conditions of the trust
              </p>
              <p
                v-if="additionalRole.hasAppointRemove"
                style="margin-top:0"
              >
                Can appoint/remove trustees
              </p>
            </div>
          </div>
          <!--  End Additional trustee(s) authority details -->
          <!-- fix this -->
          <apx-ssn
            v-if="additionalRole.ssn"
            label="Social security number"
            :value="additionalRole.ssn"
            mask-regex="\d\d\d-\d\d-"
            :mask-pattern="maskSsn"
            readonly
          />
          <apx-date-select
            v-if="additionalRole.dob"
            group-label="Date of birth"
            :date-input-value="additionalRole.dob"
            readonly
          />
          <apx-select
            v-if="additionalRole.residenceCountry"
            :value="residenceCountryName(additionalRole.residenceCountry)"
            label="Country of citizenship"
            readonly
          />
          <apx-phone
            v-if="additionalRole.dayPhone"
            telephone-number-label="Mobile number"
            :telephone-number="additionalRole.dayPhone"
            readonly
          />
          <apx-email
            v-if="additionalRole.email"
            label="Email"
            :value="additionalRole.email"
            readonly
          />
          <ReadbackComponent
            v-if="additionalRole.addresses && additionalRole.addresses.legal && !additionalRole.addresses.mailing"
            :id="`additionalRole-address-${index}`"
            label="Legal residential address"
            :values="[additionalRole.addresses.legal.line1, additionalRole.addresses.legal.line2,
                      `${additionalRole.addresses.legal.city}, ${additionalRole.addresses.legal.state}
                      ${additionalRole.addresses.legal.zip}`]"
          />
          <ReadbackComponent
            v-if="additionalRole.addresses && additionalRole.addresses.legal && additionalRole.addresses.mailing"
            :id="`additionalRole-legal-address-${index}`"
            label="Legal residential address"
            :values="[additionalRole.addresses.legal.line1, additionalRole.addresses.legal.line2,
                      `${additionalRole.addresses.legal.city}, ${additionalRole.addresses.legal.state}
                      ${additionalRole.addresses.legal.zip}`]"
          />
          <ReadbackComponent
            v-if="additionalRole.addresses && additionalRole.addresses.mailing"
            :id="`additionalRole-mailing-address-${index}`"
            :label="getMailingAddressLabel(additionalRole)"
            :values="[additionalRole.addresses.mailing.line1, additionalRole.addresses.mailing.line2,
                      `${additionalRole.addresses.mailing.city}, ${additionalRole.addresses.mailing.state}
                      ${additionalRole.addresses.mailing.zip}`]"
          />
          <!--  Additional trustee(s) employment details -->
          <apx-select
            v-if="additionalRole.employmentStatus"
            :value="pd_content.employment_status_mapping[additionalRole.employmentStatus]"
            label="Employment status"
            readonly
          />
          <apx-text-input
            v-if="showEmployerInfo(additionalRole.employmentStatus)"
            :value="additionalRole.occupation ? additionalRole.occupation : 'None'"
            label="Occupation"
            readonly
          />
          <apx-text-input
            v-if="showEmployerInfo(additionalRole.employmentStatus)"
            :value="additionalRole.employersName ? additionalRole.employersName : 'None'"
            label="Company name"
            readonly
          />
          <ReadbackComponent
            v-if="showEmployerInfo(additionalRole.employmentStatus) &&
              !additionalRole.isEmployerSameAsLegal && additionalRole.addresses.employer"
            :id="`additionalRole-employer-address-${index}`"
            label="Company address"
            :values="[additionalRole.addresses.employer.line1, additionalRole.addresses.employer.line2,
                      `${additionalRole.addresses.employer.city}, ${additionalRole.addresses.employer.state}
                      ${additionalRole.addresses.employer.zip}`]"
          />
          <ReadbackComponent
            v-if="showEmployerInfo(additionalRole.employmentStatus) && additionalRole.isEmployerSameAsLegal &&
              additionalRole.addresses.legal"
            :id="`additionalRole-employer-address-${index}`"
            label="Company address"
            :values="[additionalRole.addresses.legal.line1, additionalRole.addresses.legal.line2,
                      `${additionalRole.addresses.legal.city}, ${additionalRole.addresses.legal.state}
                      ${additionalRole.addresses.legal.zip}`]"
          />
          <ReadbackComponent
            v-if="shouldShowAssociationInfo(additionalRole) && additionalRole.employmentStatus != 'Z'"
            :id="`associated-company-info-${index}`"
            label="Investment industry association"
            :values="additionalRole.hasAssociation === 'Yes' ? [additionalRole.association] : ['None']"
            show-help
            help-header="Investment industry association"
            help-body="Our records indicate that you previously provided this information in response to
              the following question: “Are you, or an immediate family member residing in the same household,
              employed by or associated with any of the following: a broker-dealer, stock exchange, exchange member firm,
              the Financial Industry Regulatory Authority (FINRA), a municipal securities dealer, or other financial
              institution?”. If that is still accurate, you do not need to do anything more. If anything has changed,
              please use the “Edit” link to update your answer."
          />
          <ReadbackComponent
            v-if="shouldShowAssociationInfo(additionalRole)"
            :id="`corporate-control-status-${index}`"
            label="Corporate control status"
            :values="additionalRole.hasControlPersonRelationship === 'Yes'? [additionalRole.tradingSymbol] : ['None']"
            show-help
            help-header="Corporate control status"
            help-body="Our records indicate that you previously provided this information in response to the following
             question: “Are you, your spouse, or any of your relatives, which includes, but is not limited to, parents,
             in-laws, and dependents, living in your home (at the same address) a member of the board of directors, a
             10% shareholder, a policy-making officer, or someone can direct the management and policies of a publically
             traded company.” If that is still accurate, you do not need to do anything more. If anything has changed,
             please use the “Edit” link to update your answer."
          />
          <!--  Additional trustee(s) employment details -->
        </div>
        </div>
        <!--  Additional trustee authorities(s) employment details -->
        <div
          v-for="(additionalRole, index) in additionalRoles"
          :key="index+'role'">
        <div v-if="additionalRole.role === 'authority'" class="pvd-container container-divider">
          <h2
            class="pvd-heading-L"
          >
            {{ additionalRole.firstName }} {{ additionalRole.middleName }} {{ formattedTrusteeLastName(additionalRole.lastName) }}
            personal information
            <EditLink
              :page-name="additionalRole.role == 'authority' ? 'trust-authority' : 'trust-additional-role'"
              is-not-istock="true"
              :user-index="index"
              future-destination-state="trust-review-and-confirm"
            />
          </h2>
          <apx-text-input
            :value="additionalRole.role === 'trustee' ? 'Trustee':
              additionalRole.role === 'grantor'? 'Grantor':
              additionalRole.role === 'authority'? 'Additional authority':
              'Trustee and grantor'"
            label="Role in trust"
            readonly
          />
          <apx-text-input
            v-if="additionalRole.isDeceased && additionalRole.isDeceased.toUpperCase() === 'YES'"
            value="Deceased"
            label="Status"
            readonly
          />
          <div
            v-if="additionalRole.hasAmendRevoke || additionalRole.hasAppointRemove"
            class="pvd-form-row"
          >
            <!--  Additional trustee(s) authority details -->
            <div class="form-row-label form-row-label-top">
              <label>Authorities</label>
            </div>
            <div class="form-row-content">
              <p
                v-if="additionalRole.hasAmend"
                style="margin-top:0"
              >
                Can amend conditions of the trust
              </p>
              <p
                v-if="additionalRole.hasRevoke"
                style="margin-top:0"
              >
                Can revoke conditions of the trust
              </p>
              <p
                v-if="additionalRole.hasAppointRemove"
                style="margin-top:0"
              >
                Can appoint/remove trustees
              </p>
            </div>
          </div>
          <!--  End Additional trustee(s) authority details -->
          <!-- fix this -->
          <apx-ssn
            v-if="additionalRole.ssn"
            label="Social security number"
            :value="additionalRole.ssn"
            mask-regex="\d\d\d-\d\d-"
            :mask-pattern="maskSsn"
            readonly
          />
          <apx-date-select
            v-if="additionalRole.dob"
            group-label="Date of birth"
            :date-input-value="additionalRole.dob"
            readonly
          />
          <apx-select
            v-if="additionalRole.residenceCountry"
            :value="residenceCountryName(additionalRole.residenceCountry)"
            label="Country of citizenship"
            readonly
          />
          <apx-phone
            v-if="additionalRole.dayPhone"
            telephone-number-label="Mobile number"
            :telephone-number="additionalRole.dayPhone"
            readonly
          />
          <apx-email
            v-if="additionalRole.email"
            label="Email"
            :value="additionalRole.email"
            readonly
          />
          <ReadbackComponent
            v-if="additionalRole.addresses && additionalRole.addresses.legal && !additionalRole.addresses.mailing"
            :id="`additionalRole-address-${index}`"
            label="Legal residential address"
            :values="[additionalRole.addresses.legal.line1, additionalRole.addresses.legal.line2,
                      `${additionalRole.addresses.legal.city}, ${additionalRole.addresses.legal.state}
                      ${additionalRole.addresses.legal.zip}`]"
          />
          <ReadbackComponent
            v-if="additionalRole.addresses && additionalRole.addresses.legal && additionalRole.addresses.mailing"
            :id="`additionalRole-legal-address-${index}`"
            label="Legal residential address"
            :values="[additionalRole.addresses.legal.line1, additionalRole.addresses.legal.line2,
                      `${additionalRole.addresses.legal.city}, ${additionalRole.addresses.legal.state}
                      ${additionalRole.addresses.legal.zip}`]"
          />
          <ReadbackComponent
            v-if="additionalRole.addresses && additionalRole.addresses.mailing"
            :id="`additionalRole-mailing-address-${index}`"
            :label="getMailingAddressLabel(additionalRole)"
            :values="[additionalRole.addresses.mailing.line1, additionalRole.addresses.mailing.line2,
                      `${additionalRole.addresses.mailing.city}, ${additionalRole.addresses.mailing.state}
                      ${additionalRole.addresses.mailing.zip}`]"
          />
          <!--  Additional trustee(s) employment details -->
          <apx-select
            v-if="additionalRole.employmentStatus"
            :value="pd_content.employment_status_mapping[additionalRole.employmentStatus]"
            label="Employment status"
            readonly
          />
          <apx-text-input
            v-if="showEmployerInfo(additionalRole.employmentStatus)"
            :value="additionalRole.occupation ? additionalRole.occupation : 'None'"
            label="Occupation"
            readonly
          />
          <apx-text-input
            v-if="showEmployerInfo(additionalRole.employmentStatus)"
            :value="additionalRole.employersName ? additionalRole.employersName : 'None'"
            label="Company name"
            readonly
          />
          <ReadbackComponent
            v-if="showEmployerInfo(additionalRole.employmentStatus) &&
              !additionalRole.isEmployerSameAsLegal && additionalRole.addresses.employer"
            :id="`additionalRole-employer-address-${index}`"
            label="Company address"
            :values="[additionalRole.addresses.employer.line1, additionalRole.addresses.employer.line2,
                      `${additionalRole.addresses.employer.city}, ${additionalRole.addresses.employer.state}
                      ${additionalRole.addresses.employer.zip}`]"
          />
          <ReadbackComponent
            v-if="showEmployerInfo(additionalRole.employmentStatus) && additionalRole.isEmployerSameAsLegal &&
              additionalRole.addresses.legal"
            :id="`additionalRole-employer-address-${index}`"
            label="Company address"
            :values="[additionalRole.addresses.legal.line1, additionalRole.addresses.legal.line2,
                      `${additionalRole.addresses.legal.city}, ${additionalRole.addresses.legal.state}
                      ${additionalRole.addresses.legal.zip}`]"
          />
          <ReadbackComponent
            v-if="shouldShowAssociationInfo(additionalRole) && additionalRole.employmentStatus != 'Z'"
            :id="`associated-company-info-${index}`"
            label="Investment industry association"
            :values="additionalRole.hasAssociation === 'Yes' ? [additionalRole.association] : ['None']"
            show-help
            help-header="Investment industry association"
            help-body="Our records indicate that you previously provided this information in response to
              the following question: “Are you, or an immediate family member residing in the same household,
              employed by or associated with any of the following: a broker-dealer, stock exchange, exchange member firm,
              the Financial Industry Regulatory Authority (FINRA), a municipal securities dealer, or other financial
              institution?”. If that is still accurate, you do not need to do anything more. If anything has changed,
              please use the “Edit” link to update your answer."
          />
          <ReadbackComponent
            v-if="shouldShowAssociationInfo(additionalRole)"
            :id="`corporate-control-status-${index}`"
            label="Corporate control status"
            :values="additionalRole.hasControlPersonRelationship === 'Yes'? [additionalRole.tradingSymbol] : ['None']"
            show-help
            help-header="Corporate control status"
            help-body="Our records indicate that you previously provided this information in response to the following
             question: “Are you, your spouse, or any of your relatives, which includes, but is not limited to, parents,
             in-laws, and dependents, living in your home (at the same address) a member of the board of directors, a
             10% shareholder, a policy-making officer, or someone can direct the management and policies of a publically
             traded company.” If that is still accurate, you do not need to do anything more. If anything has changed,
             please use the “Edit” link to update your answer."
          />
          <!--  Additional trustee(s) employment details -->
            </div>
        </div>
        <div id="important-docs-div" class="pvd-container container-divider" v-if="routeId === 'trust'">
          <h2
            class="pvd-heading-L"
          >
            Important documents and confirmation
          </h2>
          <p>
            Carefully review the Account Opening Agreements below.
          </p>
          <ul class="pvd-list-bullet">
            <li>
              <a
                href="https://www.fidelity.com/bin-public/060_www_fidelity_com/documents/brokerage-account-customer-agreement.pdf"
                target="_blank">
                Account Agreements and Disclosures (PDF) <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Includes the Customer Agreement, Customer
              Relationship Summaries, and important information about privacy, fees, and commissions
            </li>
            <li>
              <!-- eslint-disable max-len -->
              <a
                href="https://www.actionsxchangerepository.fidelity.com/ShowDocument/ComplianceEnvelope.htm?_fax=-18%2342%23-61%23-110%23114%2378%23117%2320%23-1%2396%2339%23-62%23-21%2386%23-100%2337%2316%2335%23-68%2391%23-66%2354%23103%23-16%2369%23-30%2358%23-20%2376%23-84%23-11%23-87%230%23-50%23-20%23-92%23-98%23-116%23-28%2358%23-38%23-43%23-39%23-42%23-96%23-88%2388%23-45%23-37%2336%23-13%23122%23-127%2335%23-61%2344%23-74%235%23-89%23-105%23-67%23126%2377%23-126%23100%2345%23-44%23-73%23-15%238%23-21%23-37%23-17%23-14%23-98%23123%23-18%2345%23-59%23-82%2367%2383%23112%2317%2370%23-78%2378%23-50%2336%23-86%23-90%2381%23-21%23-119%23-30%23120%2349%2328%23-98%2333%2351%23-78%23-119%23-16%2350%23-58%2350%23102%2348%23-17%2352%23-99%23" target="_blank">
                Fidelity Government Money Market Fund Prospectus <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              <!-- eslint-enable max-len -->
              This is where your cash is held pending further
              instruction. Generally, Fidelity earns more compensation when you invest in products offered by our
              affiliates. Compensation earned, as well as your yield, varies by product
            </li>
            <li>
              <a
                href="https://www.fidelity.com/bin-public/060_www_fidelity_com/documents/terms-and-conditions.pdf" target="_blank">
                Terms & Conditions (PDF) <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Terms & Conditions applicable to opening your account
            </li>
            <li>
              <a
                href="https://www.fidelity.com/bin-public/060_www_fidelity_com/documents/fidelity/electronic-delivery-agreement.pdf"
                target="_blank">
                Electronic Delivery Agreement (PDF) <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Information about your consent to receive account related
              communication electronically rather than through the U.S. mail
            </li>
            <li>
              <a
                href="https://digital.fidelity.com/prgw/digital/aox/SSAAuthorization/details" target="_blank">
                Social Security Administration Authorization <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Authorization for the Social Security Administration to
              disclose your Social Security number verification
            </li>
            <li>
              <a
                href="https://www.fidelity.com/bin-public/060_www_fidelity_com/documents/certification-of-trust.pdf"
                target="_blank">Certification of Trust <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Certifications provided by the trustee(s) opening the account regarding the existence
              of the trust and trustee authority to act
            </li>
          </ul>
          <div>
            <p v-if="additionalTrustees.length === 0">
              By selecting Open account below, you are electronically signing this application and acknowledging that you:
            </p>
            <p v-else>
              By selecting I Agree below, you are electronically signing this application and acknowledging that you:
            </p>
            <ul class="pvd-list-bullet">
              <li>Received, understand, and agree to the Account Opening Agreements</li>
              <li>
                Agree the cash in this account will be held in the Fidelity Government Money Market Fund pending
                further instruction. Other options may be available after opening the account
              </li>
              <li>Confirm that your personal information is correct</li>
              <li>
                Agree to conduct business with Fidelity electronically and to the electronic delivery of all documents and
                communications for all your Fidelity accounts as detailed in the Electronic Delivery Agreement
              </li>
              <li>
                Consent to Fidelity's use of your email and/or mobile number to message, call or text you to help secure
                your account, provide transactional alerts, and deliver other communications. Message and data rates
                apply; frequency may vary. For help with texts, reply HELP. To opt-out of texts, log onto Fidelity.com or
                reply STOP
              </li>
              <li v-if="additionalTrustees.length === 0">
                By clicking the Open my account button you are signing the consent for SSA to disclose your SSN
                Verification to Fidelity Brokerage Services LLC and Socure Inc. You agree that your electronic signature
                has the same legal meaning, validity, and effect as your handwritten signature
              </li>
              <li v-else>
                By clicking the I Agree button you are signing the consent for SSA to disclose your SSN
                Verification to Fidelity Brokerage Services LLC and Socure Inc. You agree that your electronic signature
                has the same legal meaning, validity, and effect as your handwritten signature
              </li>
              <li>
                Are making the certification in the attached Certification of Trust as Trustee of the trust
              </li>
              <li style="font-weight: bold">
                Understand this account is governed by a pre-dispute arbitration clause contained on the last page
                of the Customer Agreement
              </li>
              <li style="font-weight: bold">
                Understand that you are certifying an IRS Form W-9 under penalties of perjury. The IRS does not
                require your consent to any provision of this document other than the certifications require to avoid
                backup withholding.
              </li>
            </ul>
          </div>
        </div>
        <!-- End Additional trustee(s) information details -->
        <div class="pvd-container" id="docusign-consent" v-if="additionalTrustees.length != 0 && routeId === 'trust'">
          <InlineMessage
            message="By continuing, you agree that Fidelity may share the information
              you've provided with trustees named in this application.
              You'll sign this application on the following screens."
            type="info"
          />
        </div>
        <div id="important-docs-div" class="pvd-container container-divider" v-if="additionalTrustees.length === 0 && routeId === 'trust'">
          <h2
            class="pvd-heading-L"
          >
            Important documents and confirmation
          </h2>
          <p>
            Carefully review the Account Opening Agreements below.
          </p>
          <ul class="pvd-list-bullet">
            <li>
              <a
                href="https://www.fidelity.com/bin-public/060_www_fidelity_com/documents/brokerage-account-customer-agreement.pdf"
                target="_blank">
                Account Agreements and Disclosures (PDF) <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Includes the Customer Agreement, Customer
              Relationship Summaries, and important information about privacy, fees, and commissions
            </li>
            <li>
              <!-- eslint-disable max-len -->
              <a
                href="https://www.actionsxchangerepository.fidelity.com/ShowDocument/ComplianceEnvelope.htm?_fax=-18%2342%23-61%23-110%23114%2378%23117%2320%23-1%2396%2339%23-62%23-21%2386%23-100%2337%2316%2335%23-68%2391%23-66%2354%23103%23-16%2369%23-30%2358%23-20%2376%23-84%23-11%23-87%230%23-50%23-20%23-92%23-98%23-116%23-28%2358%23-38%23-43%23-39%23-42%23-96%23-88%2388%23-45%23-37%2336%23-13%23122%23-127%2335%23-61%2344%23-74%235%23-89%23-105%23-67%23126%2377%23-126%23100%2345%23-44%23-73%23-15%238%23-21%23-37%23-17%23-14%23-98%23123%23-18%2345%23-59%23-82%2367%2383%23112%2317%2370%23-78%2378%23-50%2336%23-86%23-90%2381%23-21%23-119%23-30%23120%2349%2328%23-98%2333%2351%23-78%23-119%23-16%2350%23-58%2350%23102%2348%23-17%2352%23-99%23" target="_blank">
                Fidelity Government Money Market Fund Prospectus <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              <!-- eslint-enable max-len -->
              This is where your cash is held pending further
              instruction. Generally, Fidelity earns more compensation when you invest in products offered by our
              affiliates. Compensation earned, as well as your yield, varies by product
            </li>
            <li>
              <a
                href="https://www.fidelity.com/bin-public/060_www_fidelity_com/documents/terms-and-conditions.pdf" target="_blank">
                Terms & Conditions (PDF) <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Terms & Conditions applicable to opening your account
            </li>
            <li>
              <a
                href="https://www.fidelity.com/bin-public/060_www_fidelity_com/documents/fidelity/electronic-delivery-agreement.pdf"
                target="_blank">
                Electronic Delivery Agreement (PDF) <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Information about your consent to receive account related
              communication electronically rather than through the U.S. mail
            </li>
            <li>
              <a
                href="https://www.fidelity.com/bin-public/060_www_fidelity_com/documents/Integrated-Subscriber-Agreement.pdf"
                target="_blank">
                Real-Time Quotes Subscriber Agreement (PDF) <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Terms governing your use of real time quotes
            </li>
            <li>
              <a
                href="https://digital.fidelity.com/prgw/digital/aox/SSAAuthorization/details" target="_blank">
                Social Security Administration Authorization <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Authorization for the Social Security Administration to
              disclose your Social Security number verification
            </li>
            <li>
              <a
                href="https://www.fidelity.com/bin-public/060_www_fidelity_com/documents/certification-of-trust.pdf"
                target="_blank">Certification of Trust <pvd-icon aria-hidden="true" icon="new-tab"/> &nbsp;
              </a>
              Certifications provided by the trustee(s) opening the account regarding the existence
              of the trust and trustee authority to act
            </li>
          </ul>
          <div>
            <p>By selecting Open account below, you are electronically signing this application and acknowledging that you:</p>
            <ul class="pvd-list-bullet">
              <li>Received, understand, and agree to the Account Opening Agreements</li>
              <li>
                Agree the cash in this account will be held in the Fidelity Government Money Market Fund pending
                further instruction. Other options may be available after opening the account
              </li>
              <li>Confirm that your personal information is correct</li>
              <li>
                Agree to conduct business with Fidelity electronically and to the electronic delivery of all documents and
                communications for all your Fidelity accounts as detailed in the Electronic Delivery Agreement
              </li>
              <li>
                Consent to Fidelity's use of your email and/or mobile number to message, call or text you to help secure
                your account, provide transactional alerts, and deliver other communications. Message and data rates
                apply; frequency may vary. For help with texts, reply HELP. To opt-out of texts, log onto Fidelity.com or
                reply STOP
              </li>
              <li>
                By clicking the Open my account button you are signing the consent for SSA to disclose your SSN
                Verification to Fidelity Brokerage Services LLC and Socure Inc. You agree that your electronic signature
                has the same legal meaning, validity, and effect as your handwritten signature
              </li>
              <li>
                Are making the certification in the attached Certification of Trust as Trustee of the trust
              </li>
              <li style="font-weight: bold">
                Understand this account is governed by a pre-dispute arbitration clause contained on the last page
                of the Customer Agreement
              </li>
              <li style="font-weight: bold">
                Understand that you are certifying an IRS Form W-9 under penalties of perjury. The IRS does not
                require your consent to any provision of this document other than the certifications require to avoid
                backup withholding.
              </li>
            </ul>
          </div>
        </div>
        <!-- Strategic Advisers LLC details -->
        <StrategicAdviserProxyComponent
          v-if="routeId === 'managed'"
          :show-sai="showSAI"
        />
        <div
          v-if="ShowTaxDisclousureAndNotice"
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            {{ pd_content.rc_tax_withholding.tax_certification_header }}
          </h2>
          <apx-toggle
            id="toggleTax"
            v-observe-visibility="taxToggleBecameVisible"
            :value="'No'"
            :yes-label="pd_content.rc_tax_withholding.toggle_text_yes"
            :no-label="pd_content.rc_tax_withholding.toggle_text_no"
            :group-label="pd_content.rc_tax_withholding.toggle_label"
            :track-events="true"
            stack
          />
          <p
            v-if="isToggleTax === 'Yes'"
            class="xsmall"
          >
            {{ pd_content.rc_tax_withholding.tax_certification_paragraph }}
          </p>
        </div>
        <!-- Before you open your account -->
        <div
          v-if="routeId === 'managed'"
          class="pvd-container container-divider"
        >
          <h2
            id="openAcctHeader"
            class="pvd-heading-L"
          >
            {{ pd_content.rc_open_your_account_header }}
          </h2>
          <p class="m-small-top">
            {{ pd_content.rc_paragraph_lede }}
          </p>
          <ul class="pvd-list-bullet">
            <li>
              <a
                class="pvd-anchor"
                href="#"
                @click.prevent="handleCustomerAgreementClick()"
              > {{ CustAgreementText.value }}</a>{{ CustAgreementText.desc }}
              <a
                class="pvd-anchor"
                href="javascript:void(0)"
              >{{ pd_content.rc_bullet_one_documents_and_disclosures }}</a>
            </li>
            <li>
              <a
                class="pvd-anchor"
                href="#"
                @click.prevent="handleBrochureSupplementClick()"
              > {{ BrochureSupplementText }}</a>
            </li>
            <li>
              <a
                class="pvd-anchor"
                href="#"
                @click.prevent="handleTermsConditionClick()"
              > {{ TermsConditionText }}</a>
              {{ pd_content.rc_terms_and_conditions_pdf.text }}
            </li>
            <li>
              <a
                id="eDeliveryChecked"
                class="pvd-anchor"
                href="#"
                @click.prevent="handleElectronicDelivery()"
              > {{ ElectronicDeliveryText }}</a> {{ pd_content.rc_electronic_delivery_agreement_pdf.text }}
            </li>
            <li v-if="prospectusUrl && showManagedProspectusesLink">
              <a
                id="prospectus-modal-trigger"
                class="pvd-anchor"
                href="javascript:void(0);"
                @click="handleManagedProspectusClick()"
              > {{ pd_content.rc_prospectuses }}
              </a>
            </li>
            <ModalComponent
              v-if="showManagedProspectusesLink && pd_prospectuses.models.length > 0"
              :modal-header="pd_content.rc_prospectuses"
              :modal-list-link="ManagedProspectus"
              body-class="xlarge m-xxsmall-top"
              modal-class="small"
              id-val="prospectus-modal"
              @exitmodal-action="openProspectusWindow"
            />
            <ModalComponent
              v-if="showManagedProspectusesLink && pd_prospectuses.models.length === 0"
              :modal-body1="pd_content.rc_prospectus_error_message"
              body-class="xlarge m-xxsmall-top"
              modal-class="small"
              id-val="prospectus-modal"
              @exitmodal-action="openProspectusWindow"
            />
          </ul>
          <p>
            <VueShowdown
              :extensions="[formatMarkdown]"
              :markdown="pd_content.rc_open_your_account_paragragh"
            />
          </p>
        </div>
        <PageNavigation
         v-if="!isGrantorSSNMatch()"
        primary-button-type="button"
        :show-primary-button-spinner="shouldShowRepPrimaryLoadingSpinner"
        :show-secondary-button-spinner="shouldShowRepSecondaryLoadingSpinner"
        :disable-primary-button="shouldDisableRepPrimaryButton"
        :disable-secondary-button="shouldDisableRepSecondaryButton"
        primary-button-label="I Agree"
        secondary-button-label="Previous"
        :page-exit-url="pd_content.overlay_exit_link.overview_url"
        @primary-button-clicked="nextButtonClicked"
        />
        <PageNavigation
          v-if="isMaestro() && isGrantorSSNMatch()"
          primary-button-type="button"
          :primary-button-label="maestroCompletedButtonLabel"
          :show-primary-button-spinner="shouldShowRepPrimaryLoadingSpinner"
          :show-secondary-button-spinner="shouldShowRepSecondaryLoadingSpinner"
          :disable-primary-button="shouldDisableRepPrimaryButton"
          :disable-secondary-button="shouldDisableRepSecondaryButton"
          secondary-button-label="Previous"
          :page-exit-url="computedExitUrl"
          @primary-button-clicked="repPrimaryButtonClick"
          @secondary-button-clicked="repSecondaryButtonClick"
        />
        <NavigationComponent
          v-if="!isMaestro() && isGrantorSSNMatch()"
          :isPrimaryAlone="additionalTrustees.length === 0 && routeId === 'trust'"
          @open-account-button-clicked="setOpenAccountButtonClicked"
        />
        <p class="rc-footnote">
          {{ pd_content.rc_footnote }}
        </p>
      </form>
    </div>
    <div
      v-if="routeId === 'managed'"
      class="pd-spray-details"
    >
      <p
        v-if="(pd_pageData.tStoreResponse && pd_pageData.tStoreResponse.params && pd_pageData.tStoreResponse.params.accountNumber !== '' &&
          pd_pageData.tStoreResponse.params.accountNumber !== undefined)"
      >
        Pregen Account Number: {{ pd_pageData.tStoreResponse.params.accountNumber }}
      </p>
      <p>
        PAS ID:
        {{ (pd_pageData.tStoreResponse
          && pd_pageData.tStoreResponse.params
          && pd_pageData.tStoreResponse.params.pasAccountId !== ''
          && pd_pageData.tStoreResponse.params.pasAccountId !== null
          && pd_pageData.tStoreResponse.params.pasAccountId !== undefined)
          ? pd_pageData.tStoreResponse.params.pasAccountId : pd_content.rc_add_spray_details }}
      </p>
      <p>Model code: {{ pd_pageData.tStoreResponse.params.modelCode }}</p>
      <p>RO Initials: {{ !pd_pageData.tStoreResponse.params.smaStrategyCd ? pd_pageData.tStoreResponse.params.repInitials : 'ZZZ' }}</p>
      <p>{{ getEReviewNum }}</p>
    </div>
  </div>
</template>

<script>
import PageNavigation from '../components/PageNavigation.vue';
import EditLink from '../components/EditLink.vue';
import NavigationComponent from '../components/NavigationComponent.vue';
import InlineMessage from '../components/InlineMessage.vue';
import ReadbackComponent from '../components/ReadbackComponent.vue';
import PageHeading from '../components/PageHeading.vue';
import ProgressBar from '../components/ProgressBar.vue';
import SelectStateComponent from '../components/SelectStateComponent.vue';
import StrategicAdviserProxyComponent from '../components/StrategicAdviserProxyComponent.vue';
import ModalComponent from '../components/ModalComponent.vue';
import dataHelper from '../mixins/dataHelper';
import measurement from '../mixins/measurement';
import utilityFunctions from '../mixins/utility';
import constants from '../mixins/constants';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import pdfService from '../mixins/pdfService';

export default {
  name: 'TrustReviewAndConfirm',
  components: {
    NavigationComponent,
    ProgressBar,
    PageHeading,
    InlineMessage,
    ReadbackComponent,
    EditLink,
    SelectStateComponent,
    PageNavigation,
    StrategicAdviserProxyComponent,
    ModalComponent,
  },
  mixins: [dataHelper, pdfService, measurement, utilityFunctions, pageNavigationHelper],
  data() {
    return {
      openAccountButtonClicked: false,
      backButtonAttempt: false,
      idPrefix: 'agreement',
      applicant: {},
      additionalUsers: [],
      accountDetails: {},
      additionalRoles: [],
      beneficiaries: [],
      trustAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      },
      pageTitle: 'Take a moment to review and confirm your information',
      repPrimaryButtonLoading: false,
      repSecondaryButtonLoading: false,
      isToggleTax: 'No',
      additionalTrustees: [],
    };
  },
  computed: {
    computedCashPositions() {
      const cashPositions = [...(this.pd_cashPositions || [])];
      if (cashPositions.length > 0) {
        const defaultIndex = cashPositions.findIndex((cashPosition) => cashPosition.isDefault);
        const defaultSelected = cashPositions[defaultIndex];
        cashPositions.splice(defaultIndex, 1);
        cashPositions.splice(0, 0, defaultSelected);
        if (this.pu_cashPosition.length === 0) {
          this.commitPrimaryUserData(defaultSelected.symbol, 'cashPosition');
          this.commitSecondaryUserData(defaultSelected.symbol, 'cashPosition');
        }
      }
      return cashPositions;
    },
    prospectusUrl() {
      const cashPositionObj = this.computedCashPositions.find((o) => o.symbol === this.pu_cashPosition);
      if (cashPositionObj && cashPositionObj.prospectusURL && this.pu_cashPosition) {
        return cashPositionObj.prospectusURL;
      }
      return '';
    },
    showManagedProspectusesLink() {
      return this.routeId === 'managed' && this.hasManagedProspectuses(this.$globalData.tStoreResponse.params.productType);
    },
    electronicDeliveryText() {
      return (this.pd_content.rc_electronic_delivery_agreement_pdf
        ? this.pd_content.rc_electronic_delivery_agreement_pdf.value : '');
    },
    employerAddress() {
      return (this.pu_employerAddressSameAsLegal === 'Yes' && this.pu_employmentStatus === 'S')
        ? [this.pu_leg_line1, this.pu_leg_line2, this.cityStateZip(this.pu_leg_city, this.pu_leg_state, this.pu_leg_zip)] : [
          this.pu_emp_line1, this.pu_emp_line2, this.cityStateZip(this.pu_emp_city, this.pu_emp_state, this.pu_emp_zip)];
    },
    showTaxDisclousureAndNotice() {
      return this.routeId === 'brokerage' || this.routeId === 'fcma';
    },
    showDisclosure() {
      return this.pu_flowType === constants.FLOW_TYPES.EXPRESS && this.routeId !== 'managed';
    },
    maskSsn() {
      return this.uc_isCustomer ? '*****' : '';
    },
    showSecurityNumber() {
      return this.getSecurityNumber(this.pu_gNumber, this.pu_ssn);
    },
    shouldShowRepPrimaryLoadingSpinner() {
      return this.repPrimaryButtonLoading ? 'Y' : 'N';
    },
    shouldShowBase64PrimaryLoadingSpinner() {
      return !this.rcBase64ImageData ? 'Y' : 'N';
    },
    shouldDisableRepPrimaryButton() {
      return this.repPrimaryButtonLoading ? 'Y' : 'N';
    },
    shouldShowRepSecondaryLoadingSpinner() {
      return this.repSecondaryButtonLoading ? 'Y' : 'N';
    },
    cashpositiondisc() {
      return this.isManaged(this.routeId) ? '' : this.pd_content.cash_position.cash_positions_disclosure;
    },
    shouldDisableRepSecondaryButton() {
      return this.repSecondaryButtonLoading ? 'Y' : 'N';
    },
    primaryButtonLabel() {
      return (this.additionalTrustees.length === 0 && this.routeId === 'trust') ? 'Open Account' : 'I Agree';
    },
    ShowTaxDisclousureAndNotice() {
      if (this.routeId === 'managed') {
        switch (this.pd_pageData.tStoreResponse.params.regType) {
          case 'IRA':
          case 'IRRL':
          case 'IS':
          case 'ROTH':
            return false;
          default:
            return true;
        }
      }
      return this.routeId === 'brokerage' || this.routeId === 'fcma';
    },
    CustAgreementText() {
      const custagreement = {};
      custagreement.value = this.pd_content.rc_customer_agreements_pdf
        ? this.pd_content.rc_customer_agreements_pdf.value : '';
      custagreement.desc = this.pd_content.rc_bullet_one_text;
      return custagreement;
    },
    BrochureSupplementText() {
      return (this.pd_content.rc_brochure_supplement_pdf
        ? this.pd_content.rc_brochure_supplement_pdf.value : '');
    },
    TermsConditionText() {
      return (this.pd_content.rc_terms_and_conditions_pdf
        ? this.pd_content.rc_terms_and_conditions_pdf.value : '');
    },
    ElectronicDeliveryText() {
      return (this.pd_content.rc_electronic_delivery_agreement_pdf
        ? this.pd_content.rc_electronic_delivery_agreement_pdf.value : '');
    },
    ManagedProspectus() {
      const modelHoldingsName = [];
      if (this.pd_prospectuses.models && this.pd_prospectuses.models.length > 0) {
        for (let i = 0; i < this.pd_prospectuses.models[0].modelHoldings.length; i += 1) {
          modelHoldingsName.push(this.pd_prospectuses.models[0].modelHoldings[i].finstAllocation.finstId.name);
        }
        return modelHoldingsName;
      }
      return modelHoldingsName;
    },
    maestroCompletedButtonLabel() {
      return 'Ready for client review';
    },
  },
  mounted() {
    console.log('RC page mounted');
    this.$store.commit('currentState/updateField', { path: 'stateName', value: 'trust-review-and-confirm' });
    this.track(this.cs_stateName, 'pageview');
    this.removeModalClassIfExists();
    if (this.openAccountButtonClicked === false) {
      this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    }
    const toggleComponent = document.querySelector('#toggleTax');
    this.commitPrimaryUserData(false, 'taxWithHolding'); // default set to false
    if (toggleComponent) {
      toggleComponent.addEventListener('value-changed',
        this.toggleChange);
      this.toggleChange({ detail: 'No' });
    }

    // Set default core position
    const cashPositions = [...(this.pd_cashPositions || [])];
    if (cashPositions.length > 0) {
      const defaultIndex = cashPositions.findIndex((cashPosition) => cashPosition.isDefault);
      const defaultSelected = cashPositions[defaultIndex];
      if (this.pu_cashPosition.length === 0) {
        this.commitPrimaryUserData(defaultSelected.symbol, 'cashPosition');
      }
    }

    document.title = this.setTitle(this.pageTitle);
    this.applicant = this.getInstanceData().formData.primaryUser;
    this.additionalUsers = this.getInstanceData().formData.additionalUsers;
    this.additionalTrustees = this.additionalUsers.filter((user) => user.role === 'trustee' || user.role === 'trustee/grantor');
    this.additionalRoles = this.additionalUsers;
    // this.additionalUsers.filter((person) => person.role !== 'authority');
    // this.additionalRoles.push(...this.additionalUsers.filter((person) => person.role === 'authority'));
    this.beneficiaries = this.acc_details.beneficiaries;
    this.accountDetails = this.getInstanceData().formData.accountDetails;
    this.trustAddress = this.acc_details.trustAddress ? this.acc_details.trustAddress : this.trustAddress;
    this.$store.commit('currentState/updateField', { path: 'futureDestinationState', value: '' });
  },

  methods: {
    nextButtonClicked() {
      this.repPrimaryButtonLoading = true;
      this.shouldDisableRepSecondaryButton = true;
      this.isLoading = true;
      const isGrantorValid = this.isGrantorSSNMatch();
      console.log('RC Ppage Next Clicked');
      if (!isGrantorValid) {
        this.$store.commit('currentState/updateField', { path: 'destinationState', value: 'trust-ssn-mismatch' });
        this.performNavigation('grantorMismatch');
      }
    },

    isGrantorSSNMatch() {
      if (this.accountDetails.taxIDType !== 'ssn') return true;
      if (this.applicant.additionalDetails.role === 'trustee/grantor' && this.applicant.ssn === this.accountDetails.ssn) return true;
      const status = this.additionalRoles.filter((person) => ((person.role === 'grantor' && person.isDeceased === 'No') || person.role === 'trustee/grantor') && person.ssn === this.accountDetails.ssn);
      return status.length > 0;
    },
    getMailingAddressLabel(role) {
      if (role.isMailingSameAsLegal === false) {
        return 'Mailing address';
      }
      return 'Legal residential address';
    },
    setOpenAccountButtonClicked(val) {
      this.openAccountButtonClicked = val;
      this.configureBackButtonFunctionality(() => this.showBackWarning(
        this.pd_content.back_button_disabled_warning,
      ));
    },
    toggleChange(e) {
      if (e.detail) {
        this.isToggleTax = e.detail;
        const name = `${'agreement'}-confirm-${
          this.isToggleTax === 'Yes'
        }`;
        this.track(name, 'interaction');
        const value = e.detail === 'Yes';
        this.commitPrimaryUserData(value, 'taxWithHolding');
      }
    },
    taxToggleBecameVisible(isVisible) {
      if (isVisible) this.fixApexToggleAlignmment('toggleTax');
    },
    handleElectronicDelivery() {
      this.track('electronic_delivery_agreement_link', 'interaction');
      const url = this.pd_content.rc_electronic_delivery_agreement_pdf ? this.pd_content.rc_electronic_delivery_agreement_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    cityStateZip(city, state, zip) {
      const separator = state === '' && zip === '' ? '' : ',';
      return `${city}${separator} ${state} ${zip}`;
    },
    residenceCountryName(residenceCountry) {
      if (residenceCountry !== '999' && Array.isArray(this.pd_countryList.validCitizenship)) {
        return this.pd_countryList.validCitizenship
          .find((element) => element.value === residenceCountry).option;
      }
      return 'UNITED STATES';
    },
    formattedTrusteeLastName(lastName) {
      return lastName.charAt(lastName.length - 1).toUpperCase() === 'S' ? `${lastName}'` : `${lastName}'s`;
    },
    showEmployerInfo(employmentStatus) {
      return employmentStatus && employmentStatus !== 'N' && employmentStatus !== 'R';
    },
    shouldShowAssociationInfo(person) {
      return !!person.employmentStatus;
    },
    repPrimaryButtonClick() {
      this.repPrimaryButtonLoading = true;
      this.performNavigation('sendToClientForReview');
    },
    repSecondaryButtonClick() {
      this.repSecondaryButtonLoading = true;
    },
    handleCustomerAgreementClick() {
      this.track('customer_agreements_link', 'interaction');
      let url;
      if (this.isManaged(this.routeId) && this.pd_pageData.tStoreResponse && this.pd_pageData.tStoreResponse.params.productType === 'MASMA' && !this.pd_pageData.tStoreResponse.params.smaStrategyCd) {
        url = this.pd_content.rc_difs_agreements_and_disclosures ? this.pd_content.rc_difs_agreements_and_disclosures.url : '';
      } else {
        url = this.pd_content.rc_customer_agreements_pdf ? this.pd_content.rc_customer_agreements_pdf.url : '';
      }
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleBrochureSupplementClick() {
      this.track('brochure_supplement_link', 'interaction');
      const url = this.pd_content.rc_brochure_supplement_pdf ? this.pd_content.rc_brochure_supplement_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleTermsConditionClick() {
      this.track('terms_and_conditions_link', 'interaction');
      const url = this.pd_content.rc_terms_and_conditions_pdf ? this.pd_content.rc_terms_and_conditions_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleTrustedContactClick() {
      this.track('trusted_contact_link', 'interaction');
      const url = this.pd_content.rc_trusted_contact_pdf ? this.pd_content.rc_trusted_contact_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleHouseholdingOfShareholderClick() {
      this.track('householding_of_shareholde_link', 'interaction');
      const url = this.pd_content.rc_householding_of_shareholder_pdf ? this.pd_content.rc_householding_of_shareholder_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleProspectusClick(coreSymbol) {
      this.track(`${coreSymbol}_prospectus_link`, 'interaction');
      this.openWindow(this.prospectusUrl, 'documentwindow', '1024', '500');
    },
    openModal() {
      document.querySelector('#prospectus-modal').setAttribute('active', 'true');
    },
    closeModal() {
      document.querySelector('#prospectus-modal').setAttribute('active', 'false');
    },
    handleManagedProspectusClick() {
      this.openModal();
    },
    openProspectusWindow(i) {
      const firstIndObj = this.pd_prospectuses.models[0].modelHoldings[i].finstAllocation.finstId;
      const { cusip } = firstIndObj;
      const url = this.pd_envConfig.modelHoldingsURL + cusip;
      this.closeModal();
      this.openWindow(url, 'documentwindow', '650', '650');
    },
  },
};
</script>

<style scoped>
  .mt-0{
    margin-top: 0;
  }
  @media not print {
   .pd-spray-details {
     display:none;
   }
  }
  .rc-footnote {
    text-align: center;
    font-size: 1em;
  }
</style>
